import {
  ISMSMessage,
  useLazyGetNextSMSQuery,
  useLazyGetPreviousSMSQuery,
  useLazyGetSMSQuery,
  useSendSMSMutation,
} from 'app/services/sms/sms';
import { errorToast } from 'Components/Notifications';
import { uniqBy } from 'lodash';
import { useEffect, useState } from 'react';
import { IUser, useLazyGetUserProfileByIdQuery } from 'app/services/user';

interface LoadFn {
  fn: () => void;
  hasMore: boolean;
}

export default function useLoadSMS(id: number) {
  const [SendSMS] = useSendSMSMutation();
  const [messages, setMessages] = useState<ISMSMessage[]>([]);
  const [getCursorPage, { data: cursorPage, startedTimeStamp }] =
    useLazyGetSMSQuery();
  const [loadNext, { data: nextPage, isLoading: isLoadingNext }] =
    useLazyGetNextSMSQuery();
  const [loadPrevious, { data: previousPage, isLoading: isLoadingPrevious }] =
    useLazyGetPreviousSMSQuery();
  const [getUserById] = useLazyGetUserProfileByIdQuery();

  const [addScrollOffset, setAddScrollOffset] = useState(false);
  const [scrollToMessageId, setScrollToMessageId] = useState<number>();
  const [LoadNextFn, setLoadNextFn] = useState<LoadFn>({
    fn: () => {},
    hasMore: false,
  });
  const [LoadPrevFn, setLoadPrevFn] = useState<LoadFn>({
    fn: () => {},
    hasMore: false,
  });

  const clearState = () => {
    setScrollToMessageId(undefined);
    setAddScrollOffset(false);
    setMessages([]);
    setLoadNextFn({ fn: () => {}, hasMore: false });
    setLoadPrevFn({ fn: () => {}, hasMore: false });
  };

  const fetchUserDetails = async (message: ISMSMessage) => {
    try {
      const newMessage = { ...message }; // Create a shallow copy
      if (newMessage.sender_id) {
        const sender = await getUserById(newMessage.sender_id).unwrap();
        newMessage.sender = sender.user;
      }
      if (newMessage.recipient_id) {
        const recipient = await getUserById(newMessage.recipient_id).unwrap();
        newMessage.recipient = recipient.user;
      }
      return newMessage;
    } catch (error) {
      console.error('Error fetching user details:', error);
      return message; // return the original message if an error occurs
    }
  };

  const enrichMessagesWithUserDetails = async (messages: ISMSMessage[]) => {
    try {
      const enrichedMessages = await Promise.all(
        messages.map(async (message) => fetchUserDetails(message)),
      );
      return enrichedMessages;
    } catch (error) {
      console.error('Error enriching messages:', error);
      return messages;
    }
  };

  // Init
  const initCursorPage = () => {
    clearState();
    if (!id) return;
    getCursorPage([String(id)]);
  };

  // Cursor
  const onCursorPageLoad = async () => {
    if (!cursorPage || !id || cursorPage?.sms_messages.length === 0) return;
    const enrichedMessages = await enrichMessagesWithUserDetails(
      cursorPage.sms_messages,
    );
    console.log('Enriched Messages on Cursor Page Load:', enrichedMessages);
    setMessages(enrichedMessages);
    let cursorId =
      cursorPage &&
      cursorPage?.sms_messages[cursorPage?.sms_messages.length - 1]?.id;
    cursorId && setScrollToMessageId(cursorId);
  };

  // Next
  const onNextPageChange = async () => {
    if (!nextPage?.sms_messages || !id) return;
    const enrichedMessages = await enrichMessagesWithUserDetails(
      nextPage.sms_messages,
    );
    console.log('Enriched Messages on Next Page Change:', enrichedMessages);
    setMessages((messages) =>
      uniqBy((messages || []).concat(enrichedMessages), 'id'),
    );
  };

  // Previous
  const onPreviousPageChange = async () => {
    if (!previousPage?.sms_messages || !id) return;
    const enrichedMessages = await enrichMessagesWithUserDetails(
      previousPage.sms_messages,
    );
    console.log('Enriched Messages on Previous Page Change:', enrichedMessages);
    setLoadPrevFn(() => ({
      hasMore: messages[0]?.id === cursorPage?.min_id ? false : true,
      fn: () => {
        if (id) {
          setMessages((messages) =>
            uniqBy(enrichedMessages.concat(messages || []), 'id'),
          );
        }
      },
    }));
    setScrollToMessageId(messages[0]?.id);
  };

  const onMessageCreated = (message: string, recipient_id: number) => {
    SendSMS({
      recipient_id: recipient_id,
      body: message,
    }).catch((e) => errorToast(e));
  };

  useEffect(() => {
    initCursorPage();
    return clearState; // cleanup function
  }, [id]);

  useEffect(() => {
    onCursorPageLoad();
  }, [cursorPage]);

  useEffect(() => {
    onNextPageChange();
  }, [nextPage, startedTimeStamp]);

  useEffect(() => {
    onPreviousPageChange();
  }, [previousPage, startedTimeStamp]);

  const data = {
    messages: messages,
    scrollToMessageId,
    loadPrevious: LoadPrevFn.fn,
    hasMorePrevious: LoadPrevFn.hasMore,
    loadNext: LoadNextFn.fn,
    hasMoreNext: LoadNextFn.hasMore,
    isLoadingNext,
    isLoadingPrevious,
    append: onMessageCreated,
    addScrollOffset,
  };

  return data;
}
